import api from "./api";

export interface DashboardStats {
  revenue: {
    total: number;
    growth: number;
    trend: Array<{ month: string; amount: number }>;
  };
  students: {
    active: number;
    growth: number;
    weeklyTrend: Array<{ week: number; count: number }>;
  };
  courses: {
    completion: number;
    activeCourses: number;
    totalHoursWatched: number;
  };
  learning: {
    totalHoursWatched: number;
    avgCompletionTime: number;
    certificatesIssued: number;
  };
  engagement: {
    dailyActiveUsers: number;
    avgSessionDuration: number;
    assignmentsSubmitted: number;
  };
  platform: {
    uptime: number;
    avgLoadTime: number;
    errorRate: number;
  };
  satisfaction: {
    nps: number;
    overall: number;
    responseRate: number;
  };
}

// Fetch comprehensive dashboard statistics
export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    // Fetch data from multiple endpoints in parallel
    const [
      usersResponse,
      coursesResponse,
      subscriptionsResponse
    ] = await Promise.all([
      api.get("/users"),
      api.get("/courses"),
      api.get("/subscriptions")
    ]);

    const users = usersResponse.data;
    const courses = coursesResponse.data;
    const subscriptions = subscriptionsResponse.data;

    // Calculate revenue from subscriptions
    const totalRevenue = subscriptions.reduce((sum: number, sub: any) => {
      return sum + (sub.amount || 0);
    }, 0);

    const monthlyRevenue = subscriptions
      .filter((sub: any) => {
        const startDate = new Date(sub.startDate);
        const now = new Date();
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return startDate >= monthAgo;
      })
      .reduce((sum: number, sub: any) => sum + (sub.amount || 0), 0);

    // Calculate active students (users with recent activity)
    const activeStudents = users.filter((user: any) => {
      if (!user.lastLogin) return false;
      const lastLogin = new Date(user.lastLogin);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return lastLogin >= weekAgo;
    }).length;

    // Calculate course completion rate
    const totalEnrollments = courses.reduce((sum: number, course: any) => {
      return sum + (course.enrollments?.length || 0);
    }, 0);

    const completedEnrollments = courses.reduce((sum: number, course: any) => {
      return sum + (course.enrollments?.filter((enrollment: any) => 
        enrollment.progress >= 100
      ).length || 0);
    }, 0);

    const completionRate = totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0;

    // Calculate learning metrics
    const totalHoursWatched = courses.reduce((sum: number, course: any) => {
      return sum + (course.totalWatchTime || 0);
    }, 0);

    // Calculate engagement metrics
    const dailyActiveUsers = users.filter((user: any) => {
      if (!user.lastLogin) return false;
      const lastLogin = new Date(user.lastLogin);
      const today = new Date();
      return lastLogin.toDateString() === today.toDateString();
    }).length;

    // Generate trend data for the last 6 months
    const generateRevenueTrend = () => {
      const trend = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        
        const monthRevenue = subscriptions
          .filter((sub: any) => {
            const subDate = new Date(sub.startDate);
            return subDate.getFullYear() === date.getFullYear() && 
                   subDate.getMonth() === date.getMonth();
          })
          .reduce((sum: number, sub: any) => sum + (sub.amount || 0), 0);
        
        trend.push({ month: monthName, amount: monthRevenue });
      }
      return trend;
    };

    // Generate user growth trend for the last 8 weeks
    const generateUserGrowthTrend = () => {
      const trend = [];
      const now = new Date();
      for (let i = 7; i >= 0; i--) {
        const weekStart = new Date(now.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
        const weekEnd = new Date(weekStart.getTime() + (7 * 24 * 60 * 60 * 1000));
        
        const weekUsers = users.filter((user: any) => {
          if (!user.createdAt) return false;
          const userDate = new Date(user.createdAt);
          return userDate >= weekStart && userDate < weekEnd;
        }).length;
        
        trend.push({ week: 8 - i, count: weekUsers });
      }
      return trend;
    };

    // Calculate growth percentages
    const previousMonthRevenue = subscriptions
      .filter((sub: any) => {
        const startDate = new Date(sub.startDate);
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return startDate >= twoMonthsAgo && startDate < oneMonthAgo;
      })
      .reduce((sum: number, sub: any) => sum + (sub.amount || 0), 0);

    const revenueGrowth = previousMonthRevenue > 0 
      ? ((monthlyRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 
      : 0;

    const previousWeekUsers = users.filter((user: any) => {
      if (!user.lastLogin) return false;
      const lastLogin = new Date(user.lastLogin);
      const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return lastLogin >= twoWeeksAgo && lastLogin < oneWeekAgo;
    }).length;

    const studentGrowth = previousWeekUsers > 0 
      ? ((activeStudents - previousWeekUsers) / previousWeekUsers) * 100 
      : 0;

    return {
      revenue: {
        total: totalRevenue,
        growth: Math.round(revenueGrowth * 100) / 100,
        trend: generateRevenueTrend()
      },
      students: {
        active: activeStudents,
        growth: Math.round(studentGrowth * 100) / 100,
        weeklyTrend: generateUserGrowthTrend()
      },
      courses: {
        completion: Math.round(completionRate * 100) / 100,
        activeCourses: courses.filter((course: any) => course.isPublished).length,
        totalHoursWatched: Math.round(totalHoursWatched / 60) // Convert minutes to hours
      },
      learning: {
        totalHoursWatched: Math.round(totalHoursWatched / 60),
        avgCompletionTime: 4.2, // This could be calculated from actual data
        certificatesIssued: completedEnrollments
      },
      engagement: {
        dailyActiveUsers,
        avgSessionDuration: 25, // This could be calculated from actual session data
        assignmentsSubmitted: courses.reduce((sum: number, course: any) => {
          return sum + (course.assignments?.length || 0);
        }, 0)
      },
      platform: {
        uptime: 99.9, // This would come from monitoring data
        avgLoadTime: 1.2, // This would come from performance monitoring
        errorRate: 0.1 // This would come from error tracking
      },
      satisfaction: {
        nps: 75, // This would come from survey data
        overall: 4.5, // This would come from rating data
        responseRate: 85 // This would come from survey response data
      }
    };
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
};

// Fetch real-time statistics
export const getRealTimeStats = async () => {
  try {
    const [usersResponse, coursesResponse, subscriptionsResponse] = await Promise.all([
      api.get("/users"),
      api.get("/courses"),
      api.get("/subscriptions")
    ]);

    return {
      totalUsers: usersResponse.data.length,
      totalCourses: coursesResponse.data.length,
      totalSubscriptions: subscriptionsResponse.data.length,
      activeSubscriptions: subscriptionsResponse.data.filter((sub: any) => sub.status === 'active').length
    };
  } catch (error) {
    console.error("Error fetching real-time stats:", error);
    throw error;
  }
};
